import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import { FormattedMessage, useIntl } from 'react-intl'
import BungalowEquipmentInfoBox from 'routes/Accommodation/sections/BungalowEquipmentInfoBox'
import IconWithText from 'components/shared/IconWithText'
import RoomTile from './RoomTile'
import smileOrange_noBackground from 'assets/icons/smileOrange_noBackground.svg'
import { useEffect } from 'react'
import { useAccommodationSearchAvailableRoomsQuery } from 'store/api-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { Room } from 'store/api-slice/types'
import Loader from 'components/shared/Loader'
import { scrollTo } from 'helpers/animateScroll'
import { useCart } from 'store/cart-slice/hooks'
import EmptyOffer from './EmptyOffer'

const SCROLL_DURATION = 500

interface BungalowOfferProps {
    modalOpener: () => void
    onFetched: (isSuccess: boolean) => void
    index: number
    adults: number
    kids: number
    infants: number
}

const BungalowOffer = ({
    modalOpener,
    onFetched,
    index,
    adults,
    kids,
    infants,
}: BungalowOfferProps) => {
    const { formatMessage } = useIntl()
    const { language } = useApp()
    const { isStepEmpty } = useCart()

    const { dates } = useAppSelector((state) => state.accommodation)

    // const [shouldAllOffersBeVisible, setShouldAllOffersBeVisible] =
    //     useState(false)

    // const offersSwitch = () => {
    //     setShouldAllOffersBeVisible(!shouldAllOffersBeVisible)
    // }

    const getPeopleLabel = () => {
        let label = `${adults} ${
            adults === 1
                ? formatMessage({
                      id: 'global.input.adult_label',
                      defaultMessage: 'Dorosły',
                  })
                : formatMessage({
                      id: 'global.input.adults_label',
                      defaultMessage: 'Dorosłych',
                  })
        }`

        if (kids) {
            label += `, ${kids} ${
                kids === 1
                    ? formatMessage({
                          id: 'global.input.child_under_12_label',
                          defaultMessage: 'Dziecko (do 12 lat)',
                      })
                    : formatMessage({
                          id: 'global.input.children_under_12_label',
                          defaultMessage: 'Dzieci (do 12 lat)',
                      })
            }`
        }

        if (infants) {
            label += `, ${infants} ${
                infants === 1
                    ? formatMessage({
                          id: 'global.input.child_under_3_label',
                          defaultMessage: 'Dziecko (do 3 lat)',
                      })
                    : formatMessage({
                          id: 'global.input.children_under_3_label',
                          defaultMessage: 'Dzieci (do 3 lat)',
                      })
            }`
        }
        return label
    }

    const { data, isFetching, isError } =
        useAccommodationSearchAvailableRoomsQuery({
            _locale: language,
            adultCount: adults,
            childCount: kids,
            startDate: dates[0],
            endDate: dates[dates.length - 1],
        })

    const { list: rooms } = data || {}

    const roomsToDisplay: Room[] =
        rooms
            ?.map((room) => {
                const {
                    availproId,
                    description,
                    doubleBedCount,
                    pictures,
                    productId,
                    rates,
                    roomId,
                    roomName,
                    singleBedCount,
                    sofaBedCount,
                } = room

                const roomWithoutRates = {
                    availproId,
                    description,
                    doubleBedCount,
                    pictures,
                    productId,
                    roomId,
                    roomName,
                    singleBedCount,
                    sofaBedCount,
                }

                if (!rates) return []

                return rates
                    .map((rate) => {
                        const { rateName, occupancies, rateId, badges } = rate
                        const rateWithoutOccupancies = {
                            ...roomWithoutRates,
                            rateName,
                            rateId,
                            badges,
                        }

                        if (!occupancies) return []

                        return occupancies.map((occupancy) => ({
                            ...rateWithoutOccupancies,
                            ...occupancy,
                        }))
                    })
                    .flat()
            })
            .flat()
            .sort((roomA, roomB) => {
                const priceA = roomA.price || 1
                const priceB = roomB.price || 1

                return priceA - priceB
            }) || []

    const roomsCount = roomsToDisplay.length

    // const chosenRoomPerGroup = bungalows.find(
    //     ({ room }) => room?.bungalowIndex === index - 1
    // )

    // const chosenRoomIndexOnList = roomsToDisplay.findIndex(
    //     (room) =>
    //         room.occupancyId === chosenRoomPerGroup?.room?.occupancyId &&
    //         room.roomId === chosenRoomPerGroup?.room?.roomId
    // )

    useEffect(() => {
        if (isFetching) return

        if (data && !isError) {
            // if (chosenRoomIndexOnList > 1) setShouldAllOffersBeVisible(true)

            let timeout: ReturnType<typeof setTimeout>

            if (isStepEmpty('accommodation'))
                timeout = setTimeout(
                    () =>
                        scrollTo({
                            id: 'accommodation-bungalows-offer-1',
                            duration: SCROLL_DURATION,
                        }),
                    0
                )

            onFetched(true)

            return () => clearTimeout(timeout)
        }

        if (isError) onFetched(false)
    }, [isFetching, isError, data])

    return (
        <Section id={`accommodation-bungalows-offer-${index}`}>
            {isFetching || isError || !data ? (
                <Loader align="center" />
            ) : (
                <>
                    {roomsCount === 0 && <EmptyOffer index={index - 1} />}

                    {roomsCount > 0 && (
                        <Box gap={[16, 24]} mb={[24, 40]}>
                            <Box>
                                <Text
                                    variant="heading2"
                                    color="secondary_500"
                                    mb={[8, 12]}
                                >
                                    <FormattedMessage
                                        id="accommodation.bungalow_offer.bungalow_name"
                                        defaultMessage="Bungalow"
                                    />
                                    <Text
                                        as="span"
                                        variant="heading2"
                                        color="primary_500"
                                    >
                                        {' '}
                                        nr {index}
                                    </Text>
                                </Text>
                                <IconWithText
                                    icon={smileOrange_noBackground}
                                    iconAlt={formatMessage({
                                        id: 'accommodation.bungalow_offer.number_of_guests',
                                        defaultMessage: 'Liczba gości',
                                    })}
                                    text={getPeopleLabel()}
                                    textColor="grayscale_900"
                                    fontVariant="bodySmall"
                                    gap={12}
                                />
                            </Box>
                            <BungalowEquipmentInfoBox
                                modalOpener={modalOpener}
                                index={index - 1}
                            />
                            <>
                                {roomsToDisplay
                                    // ?.slice(0, 2)
                                    .map(
                                        (room, roomIndex) =>
                                            room && (
                                                <RoomTile
                                                    key={`rooms-${roomIndex}`}
                                                    room={room}
                                                    adults={adults}
                                                    kids={kids}
                                                    bungalowIndex={index}
                                                    roomIndex={roomIndex}
                                                />
                                            )
                                    )}

                                {/* {roomsCount > 0 &&
                                    roomsToDisplay.map(
                                        (room, roomIndex) =>
                                            room && (
                                                <RoomTile
                                                    key={`room-${
                                                        roomIndex + 2
                                                    }`}
                                                    room={room}
                                                    adults={adults}
                                                    kids={kids}
                                                    bungalowIndex={index}
                                                    roomIndex={roomIndex}
                                                />
                                            )
                                    )} */}
                            </>
                        </Box>
                    )}

                    {/* {roomsCount > 2 && (
                        <Box align="center">
                            <Link
                                variant="secondary"
                                icon={null}
                                onClick={offersSwitch}
                                testId={`accommo-${index - 1}-button-${
                                    shouldAllOffersBeVisible
                                        ? 'hide-some'
                                        : 'show-all'
                                }`}
                            >
                                {!shouldAllOffersBeVisible ? (
                                    <>
                                        <FormattedMessage
                                            id="accommodation.bungalow_offer.see_all_offers"
                                            defaultMessage="Zobacz wszystkie oferty"
                                        />{' '}
                                        <Text
                                            as="span"
                                            variant="bodyStrong"
                                            color="secondary_500"
                                        >
                                            ({roomsCount - 2})
                                        </Text>
                                    </>
                                ) : (
                                    <FormattedMessage
                                        id="accommodation.bungalow_offer.hide_some_offers"
                                        defaultMessage="Ukryj część ofert"
                                    />
                                )}
                            </Link>
                        </Box>
                    )} */}
                </>
            )}
        </Section>
    )
}

export default BungalowOffer
